import {useContext, useEffect, useState} from "react";
import LoginPage from "../pages/login";
import HomePage from "../pages/home";
import DailyGame from "../pages/DailyGame";
import ChallengePage from "../pages/challenge";
import {PageEnum} from "../types/IPage.ts";
import {useUserContext} from "../providers/userProvider/user-context.tsx";
import AIChallengeGame from "../pages/challenge/AIChallengeGame";
import {useMusicContext} from "../providers/musicProvider/music-player-context.tsx";
import {GAEvents} from "../common/utils/GA4/GAEvents.ts";
import {IAwardResult, IVSPlayerRoomInfo, LoginType} from "../common/utils/http/IResponse.ts";
import VsPlayerHomePage from "../pages/vsPlayer/VsPlayer.tsx";
import VSPlayerWaitingPage from "../pages/vsPlayer/components/VSPlayerWaitingPage/VSPlayerWaitingPage.tsx";
import VSPlayerShockingPage from "../pages/vsPlayer/components/VSPlayerShockingPage/VSPlayerShockingPage.tsx";
import {ThemeContext} from "../providers/themeContext/theme-context.tsx";
import {UrlHandler} from "../common/utils/urlHandler/UrlHandler.ts";
import VsPlayerGame from "../pages/vsPlayer/components/VsPlayerGame";
import WordMakerHome from "../pages/UGC/WordMakerHome.tsx";
import UGCGame from "../pages/UGC/components/UGCGame";
import {ToastContext} from "../components/CustomToast/ToastProvider.tsx";
import {UrlConfig} from "../common/config/url-config.ts";
import {apiService} from "../common/utils/http/api-service.ts";
import {CookieNameEnum} from "../common/utils/cookieManage.ts";
import RewardDialog from "../pages/home/component/RewardDialog/RewardDialog.tsx";
import debounce from "lodash/debounce";
import RewardThemeModal from "../pages/home/component/RewardThemeModal/RewardThemeModal.tsx";
// 根页面，使用根页面来管理页面的切换
const RootPage = () => {
    // 全局用户信息provider
    const {user, setUser} = useUserContext();
    // toast通知
    const {addToast} = useContext(ToastContext);
    // 主题全局管理器
    const {loadThemeConfig} = useContext(ThemeContext);
    const musicManager = useMusicContext();
    // 切换页面
    const [currentPage, setCurrentPage] = useState(PageEnum.LOGIN);
    // 显示获取徽章弹窗
    const [showMedalAwardDialog, setShowMedalAwardDialog] = useState(false);
    // 显示头像框弹窗
    const [showFrameAwardDialog, setShowFrameDialog] = useState(false);
    // 显示主题奖章颁弹窗
    const [showRewardThemeModal, setShowRewardThemeModal] = useState(false);

    // 奖励结果-徽章列表
    const [awardMedals, setAwardMedals] = useState<string[]>([]);
    // 奖励结果-头像框列表
    const [awardFrames, setAwardFrames] = useState<string[]>([]);
    // 奖励结果-主题列表
    const [awardThemes, setAwardThemes] = useState<string[]>([]);

    // 设置当前对战的 vs player信息
    const [vsPlayerInfo, setVsPlayerInfo] = useState<IVSPlayerRoomInfo | null>(null);

    const version = "1.96";

    // 调用触发器
    const triggerGAEvent = () => {
        if (!document.hidden) {
            let pageStr = "Others";
            if (currentPage === PageEnum.DAILY_GAME) {
                pageStr = "Daily Mode";
            } else if (currentPage === PageEnum.AI_CHALLENGE_GAME || currentPage === PageEnum.CHALLENGE) {
                pageStr = "Challenge Mode"
            } else if (currentPage === PageEnum.VS_PLAYER_HOME || currentPage === PageEnum.VS_PLAYER_WAITING_PAGE || currentPage === PageEnum.VS_PLAYER_SHOCK_PAGE || currentPage === PageEnum.VS_PLAYER_GAME_PAGE) {
                pageStr = "VS Player Mode"
            } else if (currentPage === PageEnum.UGCGamePage || currentPage === PageEnum.WORD_MAKER_HOME) {
                pageStr = "Word Maker Mode"
            } else {
                pageStr = "Others"
            }
            GAEvents.globalShow(pageStr);
        }
    };

    const handleVsPlayerChange = (value: IVSPlayerRoomInfo) => {
        setVsPlayerInfo(value)
    }

    // 处理页面参数，显示页面时添加时间参数，使得页面分享时的参数是新的
    const handleAddTimestamp = () => {
        // 获取当前的时间戳
        const timestamp = Date.now().toString();

        UrlHandler.setCurrentUrlParam('t', timestamp)
    };

    // 启动定时器
    useEffect(() => {
        // 每次访问都刷新网址的时间戳，避免缓存问题
        handleAddTimestamp();
        const timerId = setInterval(triggerGAEvent, 5000);

        return () => clearInterval(timerId); // 清除定时器，防止内存泄漏
    }, [currentPage]);

    // 处理页面的可见性变化
    const handleVisibilityChange = () => {
        if (document.hidden) {
            musicManager.pauseMusic();
        } else {
            musicManager.playMusic();
        }
    };

    useEffect(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // 进入时检测是否已经登录
    useEffect(() => {
        console.log(`version：${version}`)
        // 等一秒
        setTimeout(() => {
            if (user?.isLogin === true && user?.loginType === LoginType.CGTN) {
                setCurrentPage(PageEnum.HOME)
                // 加载主题配置
                loadThemeConfig();
            }
            if (user?.loginType === LoginType.CGTN) {
                getCGTNAvatar();
            }
            if (user?.isLogin) {
                getLoginReward()
            }
        }, 1000);
    }, [user?.isLogin]);

    // 获取本地cookie信息
    const getAccessToken = () => {
        // 获取所有的 cookie
        const cookies = document.cookie;
        // 查找 access_token 的值
        const accessTokenValue = cookies.match('(^|;)\\s*' + CookieNameEnum.ACCESS_TOKEN + '\\s*=\\s*([^;]+)');
        // 如果找到了 access_token，则返回它的值，否则返回空字符串
        return accessTokenValue ? accessTokenValue[0].split('=')[1].trim() : '';
    }

    const getCGTNAvatar = async () => {
        try {
            const accessToken = getAccessToken();
            // 根据获取到的token取获取用户信息
            const response = await fetch(`${UrlConfig.GET_USER_INFO_AVATAR}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Origin': UrlConfig.GET_USER_INFO_AVATAR_ORIGIN
                }
            });

            if (response.status == 200) {
                const responseJson = await response.json();
                // 处理状态码
                const statusCodeStr = responseJson['code'].toString();
                const statusCode = parseInt(statusCodeStr, 10);
                if (statusCode === 401) {
                    // 授权错误
                    // addToast({type: "error", message: "Authorization error"})
                    console.error('Authorization error');
                    return;
                }

                if (statusCode === 200) {
                    const cgtnAvatar = responseJson['data'][0]['avatar'];
                    setUser((pre) => ({
                        ...pre,
                        cgtnAvatar: cgtnAvatar
                    }))
                    // 调用后端方法更新后端头像
                    await apiService.updateUserAvatar(cgtnAvatar)
                }
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            addToast({type: "error", message: "Load user avatar failed."})
        }
    }

    // 切换页面的方法
    const switchPage = (page: PageEnum) => {
        setCurrentPage(page);
    };

    // 先切换到ugc主页，再切换到ugc游戏界main
    const nextUGCGame = () => {
        switchPage(PageEnum.WORD_MAKER_HOME)
        addToast({
            type: "warning",
            message: 'Waiting for prepare...',
        })
        setTimeout(() => {
            switchPage(PageEnum.UGCGamePage)
        }, 2000);
    };

    // 获取登录奖励
    const getLoginReward = debounce(async () => {
        const response = await apiService.getLoginReward()
        if (response.data != null) {
            handleAwardResult(response.data)
        }
    }, 500);

    // 处理分享后，获得徽章
    const handleShareReward = debounce(async () => {
        const response = await apiService.getShareReward()
        if (response.data != null) {
            handleAwardResult(response.data)
        }
    }, 1000)

    // 处理游戏胜利后，请求发放的结果
    const handleGameWinReward = debounce(async (gameType: string) => {
        const response = await apiService.getGameWinReward(gameType)
        if (response.data != null) {
            handleAwardResult(response.data)
        }
    }, 500)

    // 处理发放奖励的结果
    const handleAwardResult = (result: IAwardResult) => {
        if (result) {
            // 处理徽章
            if (result.medals && result.medals.length > 0) {
                const medalList: string[] = []
                for (const medal of result.medals) {
                    if (medal.medalUrl) {
                        medalList.push(medal.medalUrl)
                    }
                }
                if (medalList.length > 0) {
                    setAwardMedals([...medalList])
                    setShowMedalAwardDialog(true)
                }
            }
            // 处理头像框
            if (result.avatarFrames && result.avatarFrames.length > 0) {
                const avatarFrameList: string[] = []
                for (const avatarFrame of result.avatarFrames) {
                    if (avatarFrame.avatarFrameUrl) {
                        avatarFrameList.push(avatarFrame.avatarFrameUrl)
                    }
                }
                if (avatarFrameList.length > 0) {
                    setAwardFrames([...avatarFrameList])
                    setShowFrameDialog(true)
                }
            }
            // 处理主题主题皮肤
            if (result.themes && result.themes.length > 0) {
                const themeList: string[] = []
                for (const theme of result.themes) {
                    if (theme.awardImgUrl) {
                        themeList.push(theme.awardImgUrl)
                    }
                }
                if (themeList.length > 0) {
                    setAwardThemes([...themeList])
                    setShowRewardThemeModal(true)
                }
            }
        }
    };

    const renderPage = () => {
        switch (currentPage) {
            case PageEnum.HOME:
                return <HomePage switchPage={switchPage} setVsPlayerInfo={handleVsPlayerChange}/>;
            case PageEnum.LOGIN:
                return <LoginPage switchPage={switchPage}/>;
            case PageEnum.DAILY_GAME:
                return <DailyGame switchPage={switchPage} handleGameWinReward={handleGameWinReward}
                                  handleShareReward={handleShareReward}/>;
            case PageEnum.CHALLENGE:
                return <ChallengePage currentPage={currentPage} switchPage={switchPage}/>;
            case PageEnum.AI_CHALLENGE_GAME:
                return <AIChallengeGame switchPage={switchPage} handleGameWinReward={handleGameWinReward}
                                        handleShareReward={handleShareReward}/>;

            case PageEnum.VS_PLAYER_HOME:
                return <VsPlayerHomePage switchPage={switchPage} setVsPlayerInfo={handleVsPlayerChange}/>;
            case PageEnum.VS_PLAYER_WAITING_PAGE:
                return <VSPlayerWaitingPage switchPage={switchPage} vsPlayerInfo={vsPlayerInfo}
                                            setVsPlayerInfo={handleVsPlayerChange}/>;
            case PageEnum.VS_PLAYER_SHOCK_PAGE:
                return <VSPlayerShockingPage switchPage={switchPage} vsPlayerInfo={vsPlayerInfo}/>;
            case PageEnum.VS_PLAYER_GAME_PAGE:
                return <VsPlayerGame switchPage={switchPage} vsPlayerInfo={vsPlayerInfo!}/>;
            case PageEnum.WORD_MAKER_HOME:
                return <WordMakerHome switchPage={switchPage}/>;
            case PageEnum.UGCGamePage:
                return <UGCGame nextUGCGame={nextUGCGame} switchPage={switchPage}/>;


            default:
                return <LoginPage switchPage={switchPage}/>;
        }
    };

    return (
        <div>
            {renderPage()}
            {/*徽章*/}
            <RewardDialog isOpen={showMedalAwardDialog} onClose={() => setShowMedalAwardDialog(false)}
                          awardResult={awardMedals}/>
            {/*头像框*/}
            <RewardDialog isOpen={showFrameAwardDialog} onClose={() => setShowFrameDialog(false)}
                          awardResult={awardFrames}/>
            {/*主题*/}
            <RewardThemeModal isOpen={showRewardThemeModal} onClose={() => setShowRewardThemeModal(false)}
                              awardResult={awardThemes}/>
        </div>
    )
};

export default RootPage;